'use client';
import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Plane, Home, ArrowLeft, MapPin, Cloud, Navigation } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

export default function NotFound() {
    const router = useRouter();
    const { t } = useTranslation();

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-4">
            {/* Background Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-20 left-10 animate-float">
                    <Cloud className="h-12 w-12 text-muted-foreground/20" />
                </div>
                <div className="absolute top-32 right-20 animate-float-delayed">
                    <Cloud className="h-8 w-8 text-muted-foreground/15" />
                </div>
                <div className="absolute bottom-40 left-20 animate-float">
                    <Cloud className="h-16 w-16 text-muted-foreground/10" />
                </div>
                <div className="absolute bottom-20 right-10 animate-float-delayed">
                    <Navigation className="h-10 w-10 text-muted-foreground/20" />
                </div>
            </div>

            <div className="relative z-10 max-w-2xl mx-auto text-center space-y-8">
                {/* Animated Plane */}
                <div className="relative">
                    <div className="flex justify-center mb-8">
                        <div className="relative">
                            <div className="animate-bounce">
                                <Plane className="h-24 w-24 sm:h-32 sm:w-32 md:h-40 md:w-40 text-primary" />
                            </div>
                            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
                                <div className="w-16 h-2 sm:w-20 sm:h-3 bg-muted-foreground/20 rounded-full animate-pulse"></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Error Code */}
                <div className="space-y-4">
                    <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
                        404
                    </h1>
                    <div className="h-1 w-32 sm:w-40 md:w-48 bg-gradient-to-r from-primary/20 via-primary to-primary/20 rounded-full mx-auto"></div>
                </div>

                <div className="space-y-3">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground">
                        {t('404.title', 'Flight Path Not Found')}
                    </h2>
                    <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-lg mx-auto">
                        {t('404.description', 'Looks like this route has been diverted. The page you\'re looking for seems to have taken off to an unknown destination.')}
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                    <Link href="/">
                        <Button 
                            size="lg" 
                            className="w-full sm:w-auto bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl transition-all duration-200 active:scale-95 touch-manipulation"
                        >
                            <Home className="mr-2 h-5 w-5" />
                            {t('404.home', 'Return to Control Tower')}
                        </Button>
                    </Link>
                    
                    <Button 
                        variant="outline" 
                        size="lg" 
                        onClick={() => router.back()}
                        className="w-full sm:w-auto bg-background/50 backdrop-blur-sm hover:bg-accent/50 border-border/50 hover:border-border transition-all duration-200 active:scale-95 touch-manipulation"
                    >
                        <ArrowLeft className="mr-2 h-5 w-5" />
                        {t('404.back', 'Previous Route')}
                    </Button>
                </div>

                {/* Additional Help */}
                <div className="pt-8 border-t border-border/30">
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            {t('404.help_title', 'Need navigation assistance?')}
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
                            <Link 
                                href="/flights" 
                                className="text-primary hover:text-primary/80 transition-colors underline underline-offset-4 hover:underline-offset-2"
                            >
                                {t('404.flights', 'Browse Flights')}
                            </Link>
                            <span className="text-muted-foreground/50">•</span>
                            <Link 
                                href="/airports" 
                                className="text-primary hover:text-primary/80 transition-colors underline underline-offset-4 hover:underline-offset-2"
                            >
                                {t('404.airports', 'Find Airports')}
                            </Link>
                            <span className="text-muted-foreground/50">•</span>
                            <Link 
                                href="/help" 
                                className="text-primary hover:text-primary/80 transition-colors underline underline-offset-4 hover:underline-offset-2"
                            >
                                {t('404.support', 'Contact Support')}
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom Animations */}
            <style jsx>{`
                @keyframes float {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-10px); }
                }
                
                @keyframes float-delayed {
                    0%, 100% { transform: translateY(0px); }
                    50% { transform: translateY(-15px); }
                }
                
                .animate-float {
                    animation: float 6s ease-in-out infinite;
                }
                
                .animate-float-delayed {
                    animation: float-delayed 8s ease-in-out infinite;
                    animation-delay: 2s;
                }
            `}</style>
        </div>
    );
}
